import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { DependencyGraphView, GraphLegend } from "../components/shared/DependencyGraphView";
import { DependencyCard } from "../components/shared/DependencyCard";
import { RiskBadge } from "../components/shared/RiskBadge";
import { EmptyState } from "../components/shared/EmptyState";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { toast } from "sonner";
import { Network, X } from "lucide-react";

export default function MapPage() {
  const navigate = useNavigate();
  const [graph, setGraph] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/map").then(({ data }) => setGraph(data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  const selectNode = async (n) => {
    setSelected(n); setDetail(null);
    try { const { data } = await api.get(`/node/${n.id}`); setDetail(data); } catch {}
  };

  if (loading) return <div className="mono-label text-slate-500">CARGANDO MAPA…</div>;
  if (!graph || graph.nodes.length <= 1) {
    return <EmptyState icon={Network} title="Tu mapa de dependencias aparecerá aquí"
      description="Completa el diagnóstico para visualizar dónde se concentra la dependencia en tu empresa."
      action={<Button onClick={() => navigate("/audit")} className="bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">Comenzar diagnóstico</Button>} />;
  }

  const deps = graph.nodes.filter((n) => n.type === "dependency");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Dependency Map</h1>
        <p className="mt-1 text-slate-400">Toca un nodo para ver responsabilidades, decisiones y riesgo.</p>
      </div>

      <div className="card-arch p-4 sm:p-6">
        <div className="halo" />
        {/* Graph: md+ */}
        <div className="relative hidden md:block">
          <DependencyGraphView nodes={graph.nodes} edges={graph.edges} selectedNodeId={selected?.id} onSelectNode={selectNode} />
          <div className="mt-4"><GraphLegend /></div>
        </div>
        {/* Mobile fallback: list */}
        <div className="md:hidden space-y-3">
          <p className="mono-label text-slate-500">Vista de lista (móvil)</p>
          {deps.map((n, i) => (
            <button key={n.id} onClick={() => selectNode(n)} data-testid={`map-list-dep-${i}`}
              className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between">
              <span className="text-sm text-white">{n.label}</span>
              <RiskBadge severity={n.severity} />
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[#0B0D13] border-white/10 text-slate-100 overflow-y-auto w-full sm:max-w-md">
          <SheetHeader><SheetTitle className="text-white font-head">{selected?.label}</SheetTitle></SheetHeader>
          {!detail && <p className="mt-4 text-sm text-slate-500">Cargando…</p>}
          {detail?.type === "dependency" && (
            <div className="mt-4"><DependencyCard dep={detail.detail} onBuild={(d) => navigate("/app/plan")} /></div>
          )}
          {detail?.type === "person" && (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-slate-400">{detail.detail.role}</p>
              {detail.key_person_risk && (
                <div className="card-arch p-4">
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-slate-400">Key Person Risk</span>
                    <RiskBadge severity={detail.key_person_risk.label} />
                  </div>
                  <p className="mt-2 font-head text-3xl font-extrabold text-amber-400">{detail.key_person_risk.score}</p>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
                    <span>Procesos críticos: <b className="text-white">{detail.key_person_risk.critical_processes}</b></span>
                    <span>Conocimiento único: <b className="text-white">{detail.key_person_risk.unique_knowledge_areas}</b></span>
                    <span>Decisiones únicas: <b className="text-white">{detail.key_person_risk.unique_decisions}</b></span>
                    <span>Backups: <b className="text-white">{detail.key_person_risk.backups_validated}/{detail.key_person_risk.backups_total}</b></span>
                  </div>
                </div>
              )}
              {detail.dependencies?.map((d, i) => <DependencyCard key={d.id} dep={d} index={i} />)}
            </div>
          )}
          {detail?.type === "owner" && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-400">Dependencias que requieren tu implicación:</p>
              {detail.dependencies?.map((d, i) => <DependencyCard key={d.id} dep={d} index={i} />)}
            </div>
          )}
          {detail?.type === "process" && (
            <div className="mt-4 space-y-2 text-sm text-slate-300">
              <p>{detail.detail.description}</p>
              <p className="text-slate-500">Personas: {(detail.detail.people || []).join(", ") || "—"}</p>
              <p className="text-slate-500">Backup: {detail.detail.backup_exists ? "Sí" : "No"}</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
